import { useEffect } from 'react'
import type { ScreenId } from '@/types/game'

const LOCAL_KEYBOARD_SCREENS: ScreenId[] = ['game', 'pause', 'level-complete', 'game-over']
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true
  if (el instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset'].includes(el.type)
  }
  return el.isContentEditable
}

function isFocusable(el: Element): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false
  if (el.closest('[aria-hidden="true"], [inert]')) return false
  const style = window.getComputedStyle(el)
  return style.display !== 'none' && style.visibility !== 'hidden' && style.pointerEvents !== 'none'
}

export function getShortcutControls(root: ParentNode = document): HTMLElement[] {
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusable)
}

function moveFocus(delta: 1 | -1) {
  const controls = getShortcutControls()
  if (controls.length === 0) return
  const current = document.activeElement
  const index = current instanceof HTMLElement ? controls.indexOf(current) : -1
  const next = controls[(index + delta + controls.length) % controls.length]
  next.focus()
}

function clickFocusedControl() {
  const active = document.activeElement
  if (!(active instanceof HTMLElement)) return false
  if (!getShortcutControls().includes(active)) return false
  active.click()
  return true
}

export function useAppShortcuts({
  screen,
  canGoBack,
  back,
}: {
  screen: ScreenId
  canGoBack: boolean
  back: () => void
}) {
  useEffect(() => {
    if (LOCAL_KEYBOARD_SCREENS.includes(screen)) return
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return
      if (isTextEntry(e.target) && e.key !== 'Escape') return

      if (e.key === 'Escape') {
        if (canGoBack) {
          e.preventDefault()
          back()
        }
        return
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        moveFocus(1)
        return
      }

      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        moveFocus(-1)
        return
      }

      if (e.key === 'Enter' && clickFocusedControl()) {
        e.preventDefault()
      }
    }

    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [back, canGoBack, screen])
}
